import React from "react";
import useInput from "@ui/lib/hooks/useInput";
import t from "../../i18n";
import { defaultTimeZone } from "../../lib/time/intl";
import type { Project } from "../../api/useProjects";

export const useTimeZoneInput = ({
  initialTimeZone,
  projects = [],
}: {
  initialTimeZone?: string;
  projects?: Project[];
}) => {
  const hasSameValue = React.useMemo(
    () => [...new Set(projects.map(p => p.timezone).filter(d => d !== undefined))].length === 1,
    [projects],
  );
  const defaultValue = React.useMemo(() => {
    if (hasSameValue) return projects[0]?.timezone ?? "";
    if (!hasSameValue && projects.length > 1) return "";

    return initialTimeZone || defaultTimeZone;
  }, [hasSameValue, initialTimeZone, projects]);

  const {
    value: timeZoneValue,
    setValue: setTimeZoneValue,
    onChange: onTimeZoneChanged,
    dirty: isTimeZoneValueDirty,
  } = useInput(defaultValue);

  const onRollbackTimeZone = React.useCallback(() => {
    setTimeZoneValue(defaultValue);
  }, [setTimeZoneValue, defaultValue]);

  const timeZonePlaceholder = React.useMemo(() => {
    if (!hasSameValue && projects.length > 1) {
      return t("projects.modal.multiple_value");
    }

    return defaultValue;
  }, [defaultValue, hasSameValue, projects.length]);

  return {
    timeZoneValue,
    timeZonePlaceholder,
    setTimeZoneValue,
    onTimeZoneChanged,
    isTimeZoneValueDirty,
    onRollbackTimeZone,
  };
};
